import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ordersApi,
  type OrdersListParams,
  type BulkStatusResponse,
} from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const ORDERS_KEY = "admin-orders";

function ordersQueryKey(params: OrdersListParams) {
  return [ORDERS_KEY, params] as const;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Fetch paginated + filtered orders list. */
export function useOrdersList(params: OrdersListParams) {
  return useQuery({
    queryKey: ordersQueryKey(params),
    queryFn: () => ordersApi.list(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch the append-only event timeline for a single order. Safe to
 * mount eagerly — the endpoint degrades gracefully (empty events +
 * warning) when migration 122 has not been applied yet, so this hook
 * never throws for that case.
 */
export function useOrderTimeline(orderId: string | null) {
  return useQuery({
    queryKey: [ORDERS_KEY, orderId, "timeline"] as const,
    queryFn: () => ordersApi.getTimeline(orderId!),
    enabled: !!orderId,
    // 30s is long enough to avoid thrashing when the operator opens +
    // closes the drawer repeatedly, short enough that the timeline
    // updates soon after a status change trigger fires.
    staleTime: 30_000,
  });
}

/** Bulk update order status and invalidate the list cache. */
export function useBulkUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation<
    BulkStatusResponse,
    Error,
    { orderIds: readonly string[]; newStatus: string }
  >({
    mutationFn: ({ orderIds, newStatus }) =>
      ordersApi.bulkUpdateStatus(orderIds, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORDERS_KEY] });
    },
  });
}
